// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import * as React from 'react';
import {CSSProperties} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon, Popup} from 'semantic-ui-react';

import {RunSignature} from '../../types/run';
import {useRampFlagImageThumbnail} from '../../util/rampFeatureFlags';
import {toSorted} from '../../util/toSorted';
import {Thumbnails} from '../MediaCard';
import {ImageThumbnailSuccessMetadata} from '../PanelMediaBrowser/MediaBrowserPanelTileTrackingContext';
import {downloadSpritePart} from './downloadSpritePart';

function getSrcSet(thumbnails: Thumbnails, imageURL: string) {
  const thumbnailSrcSetString = toSorted(thumbnails, (a, b) => a.size - b.size)
    .map(thumbnail => `${thumbnail.directUrl} ${thumbnail.size}w`)
    .join(', ');

  // Append the original image url to the srcSet to be used for any size larger than the largest thumbnail
  return `${thumbnailSrcSetString}, ${imageURL} 9999w`;
}

const Image = ({
  width,
  step,
  imgRef,
  imageURL,
  imgStyle,
  trackSuccess,
  trackError,
  rolledBack,
  globalStep,
  filePath,
  originalWidth,
  originalPixelOffset,
  runSignature,
  boxesRef,
  masksRef,
  thumbnails,
}: {
  width: number;
  step: number;
  imgRef: React.RefObject<HTMLImageElement>;
  imageURL: string;
  imgStyle: CSSProperties;
  trackSuccess: (metadata?: ImageThumbnailSuccessMetadata) => void;
  trackError: () => void;
  rolledBack: boolean;
  globalStep: number;
  filePath: string;
  originalWidth?: number | null;
  originalPixelOffset: number;
  runSignature: RunSignature;
  boxesRef?: React.RefObject<HTMLDivElement>;
  masksRef?: React.RefObject<HTMLDivElement>;
  thumbnails?: Thumbnails;
}) => {
  const isThumbnailEnabled = useRampFlagImageThumbnail(runSignature.entityName);
  return (
    <div>
      {isThumbnailEnabled && thumbnails && thumbnails.length > 0 ? (
        <img
          data-test={`image-card-img-${step}`}
          ref={imgRef}
          srcSet={getSrcSet(thumbnails, imageURL)}
          sizes={`${width}px`}
          src={imageURL}
          alt="card"
          style={imgStyle}
          onLoad={e => {
            const usedThumbnailSizeStr =
              e.currentTarget?.currentSrc?.match(/thumbnail_(\d+)/)?.[1];
            const usedThumbnailSize = usedThumbnailSizeStr
              ? parseInt(usedThumbnailSizeStr)
              : undefined;
            trackSuccess({
              isThumbnailingEnabledForEntity: isThumbnailEnabled,
              hasThumbnails: thumbnails.length > 0,
              usedThumbnailSize,
            });
          }}
          onError={trackError}
        />
      ) : (
        <img
          data-test={`image-card-img-${step}`}
          ref={imgRef}
          src={imageURL}
          alt="card"
          style={imgStyle}
          onLoad={() => {
            trackSuccess({
              isThumbnailingEnabledForEntity: isThumbnailEnabled,
              hasThumbnails: thumbnails != null && thumbnails.length > 0,
              usedThumbnailSize: undefined,
            });
          }}
          onError={trackError}
        />
      )}
      {rolledBack && (
        <div className={'content-card__fallback'}>
          <Popup
            trigger={
              <Icon
                style={{color: 'white'}}
                size="small"
                name="exclamation circle"
              />
            }>
            No media was found for step: {globalStep}
            <br />
            The most recent step: {step} is being displayed
          </Popup>
        </div>
      )}

      {filePath && originalWidth && (
        <LegacyWBIcon
          className="content-card__download"
          onClick={() => {
            downloadSpritePart(
              runSignature,
              filePath,
              originalPixelOffset,
              originalWidth,
              {boxesRef, masksRef}
            );
          }}
          name="download"
        />
      )}
    </div>
  );
};

export default Image;
